import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import SEO from './SEO';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './CreatorsPage.css';

const THEME_ACCENTS = {
  classic: '#48bb78',
  modern: '#38b2ac',
  warm: '#ed8936',
  fresh: '#38a169',
  dark: '#a78bfa',
};

const CreatorsPage = () => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  useEffect(() => {
    fetchCreators();
  }, [debouncedSearch]);

  const fetchCreators = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/affiliate/creators?${params}`
      );
      if (!response.ok) throw new Error('Failed to load creators');
      const data = await response.json();
      setCreators(data.creators || []);
    } catch (err) {
      console.error('Error fetching creators:', err);
      setCreators([]);
    } finally {
      setLoading(false);
    }
  };

  const seo = PAGE_SEO['/creators'];

  return (
    <div className="creators-page">
      <SEO
        title={seo.title}
        description={seo.description}
        canonical="/creators"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seo.title,
            description: seo.description,
            url: 'https://freshfitlab.io/creators',
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'Creators', url: '/creators' },
          ]),
        ]}
      />
      <div className="creators-container">
        <div className="creators-page-header">
          <h1>Discover Creators</h1>
          <p>Browse recipe creators and explore their curated collections</p>
        </div>

        <div className="creators-cta-row">
          <div className="creators-cta-card">
            <div className="creators-cta-copy">
              <h2>Want your recipes featured here?</h2>
              <p>Start your public creator page and share recipes, collections, and meal-planning content.</p>
            </div>
            <Link to="/affiliate/register" className="creators-cta-link">
              Become a Coach / Creator
            </Link>
          </div>
        </div>

        <div className="creators-search-row">
          <div className="creators-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search creators..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="creators-search-clear" onClick={() => setSearchTerm('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="creators-loading">
            <div className="creators-spinner" />
            <p>Loading creators...</p>
          </div>
        ) : creators.length === 0 ? (
          <div className="creators-empty">
            <i className="fas fa-users"></i>
            <h3>{searchTerm ? 'No creators match your search' : 'No creators yet'}</h3>
            <p>Check back soon for new recipe creators!</p>
          </div>
        ) : (
          <div className="creators-grid">
            {creators.map((creator) => {
              const accent = THEME_ACCENTS[creator.theme] || THEME_ACCENTS.classic;
              return (
                <Link
                  to={`/creator/${creator.slug}`}
                  key={creator._id}
                  className="creator-card"
                >
                  <div className="creator-card-accent" style={{ background: accent }} />
                  <div className="creator-card-content">
                    {creator.logoImage ? (
                      <img
                        src={creator.logoImage}
                        alt={creator.name}
                        className="creator-card-avatar"
                      />
                    ) : (
                      <div className="creator-card-avatar creator-card-avatar--placeholder" style={{ background: accent }}>
                        <span>{(creator.name || 'C').charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                    <h3 className="creator-card-name">{creator.name}</h3>
                    {creator.headline && (
                      <p className="creator-card-headline">{creator.headline}</p>
                    )}
                    <div className="creator-card-stats">
                      <span><i className="fas fa-utensils"></i> {creator.menuCount} recipe{creator.menuCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorsPage;
